import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  Output,
  EventEmitter,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { Observable, Subject, Subscription, of } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  switchMap,
  takeUntil,
  catchError,
  map,
} from 'rxjs/operators';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ComponentCardComponent } from '../../../shared/components/common/component-card/component-card.component';
import { ButtonComponent } from '../../../shared/components/ui/button/button.component';
import { EmpresaApiService } from '../services/empresa-api.service';
import { ContactCompanyApiService } from '../services/contact-company-api.service';
import { ContactoApiService } from '../../contactos/services/contacto-api.service';
import { CompanyRelationshipTypeApiService } from '../services/company-relationship-type-api.service';
import { CompanySizeApiService } from '../services/company-size-api.service';
import { CompanySectorApiService } from '../services/company-sector-api.service';
import { CompanyTaxTypeApiService } from '../services/company-tax-type-api.service';
import { CompanyStateLeadApiService } from '../services/company-state-lead-api.service';
import { CompanyCycleApiService } from '../services/company-cycle-api.service';
import { DealDrawerService } from '../../deals/services/deal-drawer.service';
import { LeadDrawerService } from '../../leads/services/lead-drawer.service';
import {
  CompanyResponse,
  CompanyRequest,
  CompanyRelationshipType,
  CompanySize,
  CompanySector,
  CompanyTaxType,
  CompanyStateLeadResponse,
  CompanyCycleResponse,
  CompanyDuplicateCandidate,
  CompanyDuplicateCandidatesParams,
  CompanyDuplicateMatchReason,
  ContactCompanyResponse,
} from '../models/empresa.model';
import { Contact } from '../../contactos/models/contacto.model';
import { AuthService } from '../../auth-pages/services/auth.service';
import { AuthApiService } from '../../auth-pages/services/auth-api.service';
import { ToastService } from '../../../shared/services/toast.service';
import { PermissionService } from '../../../shared/services/permission.service';
import {
  EntitySelectComponent,
  EntitySelectPage,
} from '../../../shared/components/entity-select/entity-select.component';
import { CrmUserResponse } from '../../users/models/user.model';
import { CurrencyService } from '../../currency/currency.service';
import { CountryResponse } from '../../currency/models/currency.model';
import { PhoneInputFlagComponent } from '../../../shared/components/form/phone-input-flag/phone-input-flag.component';
import { COUNTRIES_DATA } from '../../../shared/data/countries.data';

// Internal type for tracking contacts in the form (pending or loaded)
interface PendingContact {
  contact: Contact;
  position: string;
  isPrimary: boolean;
  associationId?: number; // Set when loaded from API in edit mode
}

// Resumen agregado de las sub-operaciones de un guardado compuesto
// (asociaciones de contacto)
interface SubOpsResult {
  failed: number; // cuántas operaciones fallaron
  forbidden: boolean; // al menos un fallo fue 403 (sin permisos)
}

@Component({
  selector: 'app-empresa-form',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    PageBreadcrumbComponent,
    ComponentCardComponent,
    ButtonComponent,
    PhoneInputFlagComponent,
    EntitySelectComponent,
  ],
  templateUrl: './empresa-form.component.html',
})
export class EmpresaFormComponent implements OnInit, OnDestroy {
  form!: FormGroup;
  loading = false;
  saving = false;
  error = '';
  isEditing = false;
  empresaId: number | null = null;

  // Catalog lists for selects
  relationshipTypes: CompanyRelationshipType[] = [];
  companySizes: CompanySize[] = [];
  companySectors: CompanySector[] = [];
  companyTaxTypes: CompanyTaxType[] = [];
  companyStateLeads: CompanyStateLeadResponse[] = [];
  companyCycles: CompanyCycleResponse[] = [];
  loadingCatalogs = false;

  /** countryId -> nombre del país, para mostrar el país del tipo de documento elegido. */
  private countryNames = new Map<number, string>();

  /** Catálogo de países (con bandera + código telefónico) para el prefijo del teléfono. */
  readonly phoneCountries = COUNTRIES_DATA;

  // Contactos Asociados
  asociatedContacts: PendingContact[] = [];
  associationsToDelete: number[] = [];

  /**
   * Firma de los contactos vinculados tal como se cargaron del backend.
   * La lista NO forma parte del Reactive Form (se maneja con ngModel + array
   * plano), así que `form.pristine` no la refleja: sin esta firma, vincular un
   * contacto sin tocar ningún campo de la empresa se descartaba en silencio.
   */
  private asociacionesSnapshot = '';

  /**
   * Ciclo con el que se cargó la empresa. Sirve para detectar el cambio de ciclo
   * que dispara la acción configurada en el catálogo (`triggerAction`).
   */
  private originalCycleId: number | null = null;
  /** Trigger en curso: el drawer de negocio o de lead está abierto por el cambio de ciclo. */
  private pendingTrigger: 'CREATE_DEAL' | 'CREATE_LEAD' | null = null;
  /** El trigger ya se resolvió (registro creado): el siguiente guardar() no vuelve a abrirlo. */
  private triggerResolved = false;

  // --- Alerta preventiva de empresas duplicadas (Incidente 17) ---
  duplicateCandidates: CompanyDuplicateCandidate[] = [];
  duplicateDismissed = false;
  private readonly MAX_DUPLICATES_VISIBLE = 5;
  private duplicateCheckSub?: Subscription;

  // ── Drawer mode support ──────────────────────────────────────────
  @Input() drawerMode = false;
  @Input() set prefilledContactId(id: number | undefined) {
    this._prefilledContactId = id;
  }
  @Input() set prefilledContactName(name: string | undefined) {
    this._prefilledContactName = name;
  }

  @Output() saved = new EventEmitter<CompanyResponse>();
  @Output() cancelled = new EventEmitter<void>();

  private _prefilledContactId?: number;
  private _prefilledContactName?: string;

  // Contact search / typeahead
  contactSearchTerm = '';
  contactSearchResults: Contact[] = [];
  searchingContacts = false;
  loadingMoreContacts = false;
  contactSearchPage = 0;
  contactSearchTotalPages = 0;
  showContactDropdown = false;
  selectedContactToAdd: Contact | null = null;
  newContactPosition = '';
  newContactIsPrimary = false;

  private contactSearch$ = new Subject<string>();
  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private empresaApi: EmpresaApiService,
    private contactCompanyApi: ContactCompanyApiService,
    private contactoApi: ContactoApiService,
    private relationshipTypeApi: CompanyRelationshipTypeApiService,
    private sizeApi: CompanySizeApiService,
    private sectorApi: CompanySectorApiService,
    private taxTypeApi: CompanyTaxTypeApiService,
    private stateLeadApi: CompanyStateLeadApiService,
    private cycleApi: CompanyCycleApiService,
    private dealDrawerService: DealDrawerService,
    private leadDrawerService: LeadDrawerService,
    private currencyService: CurrencyService,
    private authService: AuthService,
    private authApiService: AuthApiService,
    private permissions: PermissionService,
    private router: Router,
    private route: ActivatedRoute,
    private toast: ToastService,
  ) {
    this.initForm();
  }

  // ─── Propietario / Responsable (incidente 35) ────────────────────────────

  /** Nombre visible del responsable guardado, para pintarlo en el buscador. */
  ownerDisplayName = '';

  /**
   * Reasignar una empresa es una decisión de cartera. Se exige el mismo permiso que
   * pide el backend en PATCH /api/companies/{id}/change-owner: editar sobre el módulo
   * 'companies'. Quien no lo tenga sigue viendo el responsable, pero en solo lectura.
   */
  get puedeEditarResponsable(): boolean {
    return this.permissions.has('companies', 'edit');
  }

  // Arrow functions a propósito: EntitySelectComponent las invoca como `this.searchFn(...)`
  // y un método normal perdería el `this` de este componente. El filtrado es client-side
  // porque el endpoint de usuarios no busca por texto.
  userSearch = (term: string): Observable<EntitySelectPage<CrmUserResponse>> => {
    const businessId = this.authService.getCurrentUser()?.businessId || 1;
    return this.contactoApi.getUsersByBusiness(businessId).pipe(
      map((users) => {
        const t = term.trim().toLowerCase();
        const items = t
          ? users.filter((u) =>
              `${u.fullname} ${u.lastname ?? ''} ${u.mail}`
                .toLowerCase()
                .includes(t),
            )
          : users;
        return { items, totalPages: 1 };
      }),
      catchError(() => of({ items: [] as CrmUserResponse[], totalPages: 0 })),
    );
  };

  userLabel = (u: CrmUserResponse): string =>
    `${u.fullname}${u.lastname ? ' ' + u.lastname : ''}`.trim();
  userSublabel = (u: CrmUserResponse): string => u.mail;

  /**
   * Sin botón de limpiar (`allowClear=false`): el responsable se REASIGNA, no se vacía.
   * El backend ignora `userPropietyId` nulo en el PUT para no perder el dueño en
   * actualizaciones parciales, así que una "X" prometería un borrado que nunca ocurre.
   */
  onOwnerSelected(user: CrmUserResponse | null): void {
    this.ownerDisplayName = user ? this.userLabel(user) : '';
  }

  /**
   * Escribe el nuevo responsable Y ensucia el control. `patchValue`/`setValue` son
   * programáticos: NO marcan el form como dirty, así que si el único cambio de la
   * edición era el responsable, `guardar()` se salía por la rama `form.pristine`
   * ("No se realizaron nuevos cambios") y la reasignación no llegaba nunca al PUT.
   *
   * Solo ensucia cuando hay un id nuevo: al teclear en el buscador, EntitySelect
   * emite `null` para invalidar la selección anterior, y eso no es un cambio que
   * el usuario haya pedido guardar (además el backend ignora el nulo).
   */
  onOwnerValueChange(userId: number | null): void {
    const control = this.form.get('userPropietyId');
    if (!control) return;
    const previous = control.value;
    control.setValue(userId);
    if (userId !== null && userId !== previous) {
      control.markAsDirty();
    }
  }

  /**
   * Al crear, el responsable por defecto es quien está creando la empresa — que es lo
   * que el backend asigna igualmente si el POST no manda `userPropietyId`. En edición lo
   * reemplaza `cargarEmpresa()`.
   */
  private aplicarResponsablePorDefecto(): void {
    const currentUser = this.authService.getCurrentUser();
    if (!currentUser) return;
    this.form.patchValue({ userPropietyId: currentUser.id });
    this.ownerDisplayName =
      `${currentUser.fullname} ${currentUser.lastname || ''}`.trim();
  }

  /**
   * Resuelve el nombre del responsable guardado. Una empresa sin responsable (creada por
   * un formulario antiguo, importada…) deja el campo vacío: el placeholder ya invita a
   * asignar uno, y rellenarlo con el usuario logeado sería mentir sobre quién la atiende.
   */
  private cargarNombreResponsable(ownerId?: number): void {
    const currentUser = this.authService.getCurrentUser();
    if (!ownerId) {
      this.ownerDisplayName = '';
      return;
    }
    if (ownerId === currentUser?.id) {
      this.ownerDisplayName =
        `${currentUser.fullname} ${currentUser.lastname || ''}`.trim();
      return;
    }
    this.authApiService.getUserById(ownerId).subscribe({
      next: (u) => {
        this.ownerDisplayName = `${u.fullname} ${u.lastname || ''}`.trim();
      },
      error: () => {
        // El directorio resuelve también a los inactivos: fallar aquí es «ya no existe».
        this.ownerDisplayName = 'Usuario no disponible';
      },
    });
  }

  ngOnInit(): void {
    this.cargarPaises();
    this.cargarCatalogos();
    this.aplicarResponsablePorDefecto();

    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.isEditing = true;
      this.empresaId = +id;
      this.cargarEmpresa(this.empresaId);
      this.cargarContactosAsociados(this.empresaId);
    }

    // Cuando el drawer guarda el negocio/lead disparado por el trigger del ciclo,
    // se reanuda el guardado de la empresa (mismo flujo que en contactos).
    this.dealDrawerService.dealSaved$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.pendingTrigger === 'CREATE_DEAL') {
          this.pendingTrigger = null;
          this.triggerResolved = true;
          this.guardar();
        }
      });
    this.leadDrawerService.leadSaved$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.pendingTrigger === 'CREATE_LEAD') {
          this.pendingTrigger = null;
          this.triggerResolved = true;
          this.guardar();
        }
      });

    this.contactSearch$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((term) => {
          this.contactSearchPage = 0;
          this.contactSearchResults = [];
          this.contactSearchTotalPages = 0;

          if (term.length === 0) {
            this.showContactDropdown = false;
            return of(null);
          }

          this.searchingContacts = true;
          const businessId = this.authService.getCurrentUser()?.businessId || 1;
          return this.contactoApi
            .searchContacts({ businessId, searchTerm: term, page: 0, size: 10 })
            .pipe(catchError(() => of(null)));
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((response) => {
        this.searchingContacts = false;
        if (response) {
          const addedIds = this.asociatedContacts.map((pc) => pc.contact.id);
          this.contactSearchResults = response.content.filter(
            (c) => !addedIds.includes(c.id),
          );
          this.contactSearchTotalPages = response.totalPages;
          this.showContactDropdown = this.contactSearchResults.length > 0;
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Catálogo global de países (compartido entre tenants) para resolver el país del tipo de documento. */
  private cargarPaises(): void {
    this.currencyService.getCountries().subscribe({
      next: (countries: CountryResponse[]) => {
        for (const c of countries) this.countryNames.set(c.id, c.name);
      },
      // Silencioso: si falla, el form sigue funcionando sin el nombre del país.
      error: () => {},
    });
  }

  /** País del tipo de documento actualmente seleccionado (vacío si no hay tipo o no tiene país). */
  get selectedTaxTypeCountry(): string {
    const taxTypeId = this.form?.get('idCompanyTaxType')?.value as
      | number
      | null;
    if (taxTypeId == null) return '';
    const taxType = this.companyTaxTypes.find((t) => t.id === taxTypeId);
    if (!taxType?.countryId) return '';
    return this.countryNames.get(taxType.countryId) ?? '';
  }

  private cargarCatalogos(): void {
    this.loadingCatalogs = true;

    const businessId = this.authService.getCurrentUser()?.businessId || 1;

    Promise.all([
      this.relationshipTypeApi.getAll().toPromise(),
      this.sizeApi.getAll().toPromise(),
      this.sectorApi.getAll().toPromise(),
      this.taxTypeApi.getAll().toPromise(),
      this.stateLeadApi.getAll().toPromise(),
      this.cycleApi.getAll(businessId).toPromise(),
    ])
      .then(
        ([relationshipTypes, sizes, sectors, taxTypes, stateLeads, cycles]) => {
          this.relationshipTypes = relationshipTypes?.content || [];
          this.companySizes = sizes?.content || [];
          this.companySectors = sectors || [];
          this.companyTaxTypes = taxTypes?.content || [];
          this.companyStateLeads = stateLeads?.content || [];
          this.companyCycles = cycles?.content || [];
          this.loadingCatalogs = false;
          if (this.drawerMode && !this.isEditing) {
            this.applyPrefill();
          }
        },
      )
      .catch(() => {
        this.loadingCatalogs = false;
        this.error = 'Error al cargar catálogos';
      });
  }

  private initForm(): void {
    this.form = this.fb.group({
      commercialName: ['', [Validators.required, Validators.maxLength(200)]],
      businessName: ['', [Validators.required, Validators.maxLength(200)]],
      description: ['', Validators.maxLength(this.MAX_DESCRIPTION)],
      phone: ['', Validators.maxLength(20)],
      fiscalAddress: ['', Validators.maxLength(this.MAX_FISCAL_ADDRESS)],
      website: ['', Validators.maxLength(200)],
      idCompanyTaxType: [null],
      taxCode: ['', Validators.maxLength(50)],
      idCompanyRelationshipType: [null],
      idCompanySize: [null],
      idCompanySector: [null],
      idCompanyStateLead: [null],
      idCompanyCycle: [null],
      userPropietyId: [null],
    });
    this.setupDuplicateCheck();
  }

  // ─── Contadores de caracteres (incidentes 33 y 34) ───────────────────────
  // Los dos límites son los que valida el backend en CompanyRequest (@Size).
  // A propósito NO se pone `maxlength` en el HTML: recortar en silencio era
  // justo la queja del inc33 («no me deja escribir y no me dice por qué»).
  // Se deja escribir de más, el contador se pone en rojo y el mensaje explica
  // cuántos caracteres sobran.
  readonly MAX_DESCRIPTION = 6000;
  readonly MAX_FISCAL_ADDRESS = 5000;

  charCount(field: string): number {
    const value = this.form?.get(field)?.value;
    return typeof value === 'string' ? value.length : 0;
  }

  /** true cuando el campo ya superó su límite (sin esperar a que se toque). */
  excedeLimite(field: string, max: number): boolean {
    return this.charCount(field) > max;
  }

  // ─── Alerta preventiva de empresas duplicadas (Incidente 17) ─────────────
  // Best-effort: nunca bloquea el guardado; errores se ignoran en silencio.
  // Se re-suscribe cada vez que initForm() recrea el FormGroup (reset del drawer).
  private setupDuplicateCheck(): void {
    this.duplicateCheckSub?.unsubscribe();
    this.duplicateCheckSub = this.form.valueChanges
      .pipe(
        debounceTime(400),
        map((v) => ({
          commercialName: ((v.commercialName as string) || '').trim(),
          businessName: ((v.businessName as string) || '').trim(),
          website: ((v.website as string) || '').trim(),
          taxCode: ((v.taxCode as string) || '').trim(),
        })),
        distinctUntilChanged(
          (a, b) =>
            a.commercialName === b.commercialName &&
            a.businessName === b.businessName &&
            a.website === b.website &&
            a.taxCode === b.taxCode,
        ),
        switchMap((criteria) => {
          // Nombres cuentan como criterio solo con 3+ caracteres
          const commercialName =
            criteria.commercialName.length >= 3
              ? criteria.commercialName
              : undefined;
          const businessName =
            criteria.businessName.length >= 3
              ? criteria.businessName
              : undefined;
          const website = criteria.website || undefined;
          const taxCode = criteria.taxCode || undefined;

          if (!commercialName && !businessName && !website && !taxCode) {
            // Sin criterios: ocultar el panel sin llamar a la API
            this.duplicateCandidates = [];
            return of(null);
          }

          const businessId = this.authService.getCurrentUser()?.businessId || 1;
          const params: CompanyDuplicateCandidatesParams = { businessId };
          if (commercialName !== undefined)
            params.commercialName = commercialName;
          if (businessName !== undefined) params.businessName = businessName;
          if (website !== undefined) params.website = website;
          if (taxCode !== undefined) params.taxCode = taxCode;
          if (this.isEditing && this.empresaId)
            params.excludeId = this.empresaId;

          return this.empresaApi.getDuplicateCandidates(params).pipe(
            catchError(() => of(null)), // silencio: la alerta es best-effort
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((candidates) => {
        if (candidates === null) return; // error o sin criterios: no cambiar nada más
        const prevIds = this.duplicateCandidates.map((c) => c.id).join(',');
        const newIds = candidates.map((c) => c.id).join(',');
        this.duplicateCandidates = candidates;
        // Si el conjunto de candidatos cambió, volver a mostrar el panel aunque se haya cerrado
        if (prevIds !== newIds) {
          this.duplicateDismissed = false;
        }
      });
  }

  get showDuplicateWarning(): boolean {
    return this.duplicateCandidates.length > 0 && !this.duplicateDismissed;
  }

  get visibleDuplicateCandidates(): CompanyDuplicateCandidate[] {
    return this.duplicateCandidates.slice(0, this.MAX_DUPLICATES_VISIBLE);
  }

  get duplicateExtraCount(): number {
    return Math.max(
      0,
      this.duplicateCandidates.length - this.MAX_DUPLICATES_VISIBLE,
    );
  }

  dismissDuplicateWarning(): void {
    this.duplicateDismissed = true;
  }

  matchReasonLabel(reason: CompanyDuplicateMatchReason): string {
    switch (reason) {
      case 'EXACT_NAME':
        return 'Nombre idéntico';
      case 'SIMILAR_NAME':
        return 'Nombre similar';
      case 'SAME_DOMAIN':
        return 'Mismo dominio web';
      case 'SAME_TAX_CODE':
        return 'Mismo código fiscal';
      default:
        return reason;
    }
  }

  verEmpresaDuplicada(id: number): void {
    // Nueva pestaña en ambos modos: preserva lo que el usuario está escribiendo
    // (en página /empresas/editar/:id el componente se reutilizaría y quedaría stale).
    window.open(`#/empresas/editar/${id}`, '_blank');
  }

  cargarEmpresa(id: number): void {
    this.loading = true;
    this.empresaApi.getCompanyById(id).subscribe({
      next: (empresa: CompanyResponse) => {
        this.form.patchValue({
          commercialName: empresa.commercialName,
          businessName: empresa.businessName,
          description: empresa.description,
          phone: empresa.phone,
          fiscalAddress: empresa.fiscalAddress,
          website: empresa.website,
          idCompanyTaxType: empresa.idCompanyTaxType,
          taxCode: empresa.taxCode,
          idCompanyRelationshipType: empresa.idCompanyRelationshipType,
          idCompanySize: empresa.idCompanySize,
          idCompanySector: empresa.idCompanySector,
          idCompanyStateLead: empresa.idCompanyStateLead,
          idCompanyCycle: empresa.idCompanyCycle,
          userPropietyId: empresa.userPropietyId ?? null,
        });
        this.originalCycleId = empresa.idCompanyCycle ?? null;
        this.cargarNombreResponsable(empresa.userPropietyId);
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar la empresa';
        this.loading = false;
      },
    });
  }

  private cargarContactosAsociados(idCompany: number): void {
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.contactCompanyApi.getByCompany(idCompany, businessId).subscribe({
      next: (associations: ContactCompanyResponse[]) => {
        this.asociatedContacts = associations.map((assoc) => ({
          contact: assoc.contact
            ? { ...assoc.contact, businessId: assoc.businessId }
            : {
                id: assoc.idContact,
                fullname: `Contacto #${assoc.idContact}`,
                mail: '',
                businessId: assoc.businessId,
              },
          position: assoc.position || '',
          isPrimary: assoc.isPrimary,
          associationId: assoc.id,
        }));
        this.asociacionesSnapshot = this.firmaAsociaciones();
      },
      error: () => {
        /* Non-fatal - show empty list */
      },
    });
  }

  /** Estado comparable de la lista de contactos vinculados. */
  private firmaAsociaciones(): string {
    return this.asociatedContacts
      .map(
        (pc) =>
          `${pc.associationId ?? 'new'}:${pc.contact.id}:${pc.position}:${pc.isPrimary}`,
      )
      .join('|');
  }

  /** true si se agregó, quitó o editó algún contacto vinculado. */
  private asociacionesCambiaron(): boolean {
    return (
      this.associationsToDelete.length > 0 ||
      this.firmaAsociaciones() !== this.asociacionesSnapshot
    );
  }

  // --- Contact search typeahead ---

  onContactSearchInput(value: string): void {
    this.contactSearchTerm = value;
    this.selectedContactToAdd = null;
    this.contactSearch$.next(value);
  }

  onContactSearchEnter(event: Event): void {
    event.preventDefault();
    const businessId = this.authService.getCurrentUser()?.businessId || 1;

    this.contactSearchPage = 0;
    this.contactSearchResults = [];
    this.contactSearchTotalPages = 0;
    this.searchingContacts = true;
    this.showContactDropdown = false;

    this.contactoApi
      .searchContacts({
        businessId,
        searchTerm: this.contactSearchTerm.trim(),
        page: 0,
        size: 10,
      })
      .pipe(
        catchError(() => of(null)),
        takeUntil(this.destroy$),
      )
      .subscribe((response) => {
        this.searchingContacts = false;
        if (response) {
          const addedIds = this.asociatedContacts.map((pc) => pc.contact.id);
          this.contactSearchResults = response.content.filter(
            (c) => !addedIds.includes(c.id),
          );
          this.contactSearchTotalPages = response.totalPages;
          this.showContactDropdown = this.contactSearchResults.length > 0;
        }
      });
  }

  onDropdownScroll(event: Event): void {
    const el = event.target as HTMLElement;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 40;
    if (!nearBottom || this.loadingMoreContacts) return;
    if (this.contactSearchPage + 1 >= this.contactSearchTotalPages) return;

    this.loadingMoreContacts = true;
    this.contactSearchPage++;
    const businessId = this.authService.getCurrentUser()?.businessId || 1;

    this.contactoApi
      .searchContacts({
        businessId,
        searchTerm: this.contactSearchTerm.trim(),
        page: this.contactSearchPage,
        size: 10,
      })
      .pipe(
        catchError(() => of(null)),
        takeUntil(this.destroy$),
      )
      .subscribe((response) => {
        this.loadingMoreContacts = false;
        if (response) {
          const addedIds = this.asociatedContacts.map((pc) => pc.contact.id);
          const newResults = response.content.filter(
            (c) => !addedIds.includes(c.id),
          );
          this.contactSearchResults = [
            ...this.contactSearchResults,
            ...newResults,
          ];
          this.contactSearchTotalPages = response.totalPages;
        }
      });
  }
  selectContact(contact: Contact): void {
    this.selectedContactToAdd = contact;
    const displayName = `${contact.fullname}${contact.lastname ? ' ' + contact.lastname : ''} (${contact.mail})`;
    this.contactSearchTerm = displayName;
    this.showContactDropdown = false;
    this.contactSearchResults = [];
  }

  agregarContacto(): void {
    if (!this.selectedContactToAdd) return;

    const newEntry: PendingContact = {
      contact: this.selectedContactToAdd,
      position: this.newContactPosition.trim(),
      isPrimary: this.newContactIsPrimary,
    };

    if (newEntry.isPrimary) {
      this.asociatedContacts.forEach((pc) => (pc.isPrimary = false));
    }

    this.asociatedContacts.push(newEntry);

    // Reset fields
    this.selectedContactToAdd = null;
    this.contactSearchTerm = '';
    this.newContactPosition = '';
    this.newContactIsPrimary = false;
    this.contactSearchResults = [];
    this.showContactDropdown = false;
  }

  quitarContacto(index: number): void {
    const entry = this.asociatedContacts[index];
    if (entry.associationId) {
      this.associationsToDelete.push(entry.associationId);
    }
    this.asociatedContacts.splice(index, 1);
  }

  togglePrimary(index: number): void {
    const wasPrimary = this.asociatedContacts[index].isPrimary;
    this.asociatedContacts.forEach((pc) => (pc.isPrimary = false));
    this.asociatedContacts[index].isPrimary = !wasPrimary;
  }

  getContactInitials(contact: Contact): string {
    const first = contact.fullname
      ? contact.fullname.charAt(0).toUpperCase()
      : '';
    const last = contact.lastname
      ? contact.lastname.charAt(0).toUpperCase()
      : contact.fullname.length > 1
        ? contact.fullname.charAt(1).toUpperCase()
        : '';
    return first + last;
  }
  getContactAvatarColor(contact: Contact): string {
    const colors = [
      'bg-brand-100 text-brand-700 dark:bg-brand-900/30 dark:text-brand-400',
      'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400',
      'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400',
      'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
      'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
    ];
    const name = contact.fullname || '';
    const charSum = name
      .split('')
      .reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
    return colors[charSum % colors.length];
  }

  hideDropdownDelayed(): void {
    setTimeout(() => {
      this.showContactDropdown = false;
    }, 200);
  }

  /** Nombre visible de un contacto asociado (para prellenar el drawer de negocio/lead). */
  private nombreContacto(contact: Contact): string {
    return `${contact.fullname ?? ''}${contact.lastname ? ' ' + contact.lastname : ''}`.trim();
  }

  guardar(): void {
    if (this.form.invalid) {
      // inc34 — antes se salía en silencio: el botón "Crear"/"Actualizar" no hacía
      // nada y el usuario no tenía forma de saber que le sobraban caracteres.
      this.form.markAllAsTouched();
      this.error = this.resumenErrores();
      this.toast.error({ title: 'Revisa el formulario', message: this.error });
      return;
    }

    // Acción configurada en el ciclo de empresa (CREATE_DEAL / CREATE_LEAD): al mover
    // la empresa a un ciclo con trigger, primero se abre el drawer correspondiente y
    // solo cuando ese registro se guarda se persiste el cambio de ciclo. Mismo flujo
    // que contactos (contacto-form).
    if (this.isEditing && this.empresaId && !this.triggerResolved) {
      const newCycleId = this.form.value.idCompanyCycle ?? null;
      const cycle = this.companyCycles.find((c) => c.id === newCycleId);
      const trigger = cycle?.triggerAction;
      if (
        newCycleId &&
        newCycleId !== this.originalCycleId &&
        (trigger === 'CREATE_DEAL' || trigger === 'CREATE_LEAD')
      ) {
        const primary =
          this.asociatedContacts.find((pc) => pc.isPrimary) ||
          this.asociatedContacts[0];
        const opts = {
          companyId: this.empresaId,
          companyName: this.form.value.commercialName || '',
          contactId: primary?.contact?.id,
          contactName: primary ? this.nombreContacto(primary.contact) : undefined,
        };
        this.pendingTrigger = trigger;
        if (trigger === 'CREATE_DEAL') {
          this.dealDrawerService.open(opts);
        } else {
          this.leadDrawerService.open(opts);
        }
        return;
      }
    }
    this.triggerResolved = false;

    this.saving = true;
    this.error = '';

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    const formValue = { ...this.form.value };
    // Sin permiso para reasignar, el responsable ni siquiera viaja en el body: el
    // backend ignora userPropietyId nulo y conserva el dueño actual.
    if (!this.puedeEditarResponsable) {
      delete formValue.userPropietyId;
    }

    const companyRequest: CompanyRequest = {
      ...formValue,
      businessId,
    };
    // La lista de contactos vinculados vive fuera del Reactive Form: hay que
    // consultarla aparte o los cambios solo en esa lista se perderían.
    if (this.form.pristine && !this.asociacionesCambiaron()) {
      this.saving = false;
      this.toast.info({
        title: 'Empresa',
        message: 'No se realizaron nuevos cambios en la empresa',
      });
      if (this.drawerMode) {
        this.cancelled.emit();
      } else {
        this.router.navigate(['/empresas']);
      }
      return;
    } else if (this.isEditing && this.empresaId) {
      this.empresaApi.updateCompany(this.empresaId, companyRequest).subscribe({
        next: (updatedCompany: CompanyResponse) => {
          this.guardarAsociaciones(
            this.empresaId!,
            businessId,
            true,
            'actualizó',
            updatedCompany,
          );
        },
        error: (err) => {
          this.saving = false;
          this.error = err.error?.message || 'Error al actualizar la empresa';
          this.toast.error({ title: 'Error', message: this.error });
        },
      });
    } else {
      this.empresaApi.createCompany(companyRequest).subscribe({
        next: (createdCompany: CompanyResponse) => {
          this.guardarAsociaciones(
            createdCompany.id,
            businessId,
            false,
            'creó',
            createdCompany,
          );
        },
        error: (err) => {
          this.saving = false;
          this.error = err.error?.message || 'Error al registrar la empresa';
          this.toast.error({ title: 'Error', message: this.error });
        },
      });
    }
  }

  private guardarAsociaciones(
    idCompany: number,
    businessId: number,
    isEdit: boolean,
    accion: 'creó' | 'actualizó',
    savedCompany?: CompanyResponse,
  ): void {
    const deletePromises: Promise<unknown>[] = this.associationsToDelete.map(
      (assocId) =>
        this.contactCompanyApi
          .deleteAssociation(assocId, businessId)
          .toPromise()
          .then(() => null)
          .catch((err) => err),
    );

    // New contacts to create (no associationId yet)
    const toCreate = isEdit
      ? this.asociatedContacts.filter((pc) => !pc.associationId)
      : this.asociatedContacts;

    const createPromises: Promise<unknown>[] = toCreate
      .filter((pc) => pc.contact.id != null)
      .map((pc) =>
        this.contactCompanyApi
          .createAssociation({
            idContact: pc.contact.id!,
            idCompany,
            businessId,
            isPrimary: pc.isPrimary,
            position: pc.position || undefined,
          })
          .toPromise()
          .then(() => null)
          .catch((err) => err),
      );

    // Update existing associations (edit mode only)
    const updatePromises: Promise<unknown>[] = isEdit
      ? this.asociatedContacts
          .filter((pc) => pc.associationId && pc.contact.id != null)
          .map((pc) =>
            this.contactCompanyApi
              .updateAssociation(
                pc.associationId!,
                {
                  idContact: pc.contact.id!,
                  idCompany,
                  businessId,
                  isPrimary: pc.isPrimary,
                  position: pc.position || undefined,
                },
                businessId,
              )
              .toPromise()
              .then(() => null)
              .catch((err) => err),
          )
      : [];

    Promise.all([...deletePromises, ...createPromises, ...updatePromises]).then(
      (results) => {
        this.saving = false;
        this.notificarResultado(accion, this.resumirFallos(results));
        if (this.drawerMode && savedCompany) {
          this.saved.emit(savedCompany);
        } else {
          this.router.navigate(['/empresas']);
        }
      },
    );
  }

  /**
   * Convierte los resultados de sub-operaciones (null = OK, HttpErrorResponse =
   * fallo) en un resumen. Un fallo no aborta al resto: cada promesa ya trae su
   * propio catch.
   */
  private resumirFallos(results: unknown[]): SubOpsResult {
    const errors = results.filter(
      (r): r is { status?: number } => r !== null && r !== undefined,
    );
    if (errors.length > 0) {
      console.warn(
        `[EmpresaForm] ${errors.length} operación(es) de contactos vinculados fallaron al guardar:`,
        errors,
      );
    }
    return {
      failed: errors.length,
      forbidden: errors.some((e) => e?.status === 403),
    };
  }

  /**
   * Feedback consolidado: la empresa ya se guardó en este punto, pero el toast
   * distingue el éxito total del guardado parcial (fallaron los vínculos).
   */
  private notificarResultado(
    accion: 'creó' | 'actualizó',
    contactos: SubOpsResult,
  ): void {
    if (contactos.failed === 0) {
      if (accion === 'creó') {
        this.toast.created('Empresa');
      } else {
        this.toast.updated('Empresa');
      }
      return;
    }

    let message = `La empresa se ${accion}, pero no se pudieron guardar los contactos vinculados.`;
    if (contactos.forbidden) {
      message += ' No tienes permiso para editar contactos.';
    }
    this.toast.warning({ title: 'Guardado parcial', message });
  }

  cancelar(): void {
    if (this.drawerMode) {
      this.cancelled.emit();
    } else {
      this.router.navigate(['/empresas']);
    }
  }

  /** Called by drawer before opening */
  reset(): void {
    this.isEditing = false;
    this.empresaId = null;
    this.asociatedContacts = [];
    this.associationsToDelete = [];
    this.asociacionesSnapshot = '';
    this.contactSearchTerm = '';
    this.selectedContactToAdd = null;
    this.newContactPosition = '';
    this.newContactIsPrimary = false;
    this.contactSearchResults = [];
    this.showContactDropdown = false;
    this.error = '';
    this.saving = false;
    this.duplicateCandidates = [];
    this.duplicateDismissed = false;
    this.ownerDisplayName = '';
    this.initForm();
    this.aplicarResponsablePorDefecto();
  }

  /** Called by drawer in edit mode */
  loadEmpresa(id: number): void {
    this.isEditing = true;
    this.empresaId = id;
    this.cargarEmpresa(id);
    this.cargarContactosAsociados(id);
  }

  private applyPrefill(): void {
    if (this._prefilledContactId) {
      this.asociatedContacts = [
        {
          contact: {
            id: this._prefilledContactId,
            fullname:
              this._prefilledContactName?.split(' ')[0] ||
              `Contacto #${this._prefilledContactId}`,
            lastname:
              this._prefilledContactName?.split(' ').slice(1).join(' ') || '',
            mail: '',
            businessId: this.authService.getCurrentUser()?.businessId || 1,
          },
          position: '',
          isPrimary: true,
        },
      ];
    }
  }

  hasError(field: string): boolean {
    const control = this.form.get(field);
    return control ? control.invalid && control.touched : false;
  }

  getError(field: string): string {
    const control = this.form.get(field);
    if (!control || !control.errors) return '';
    if (control.errors['required']) return 'Este campo es requerido';
    if (control.errors['email']) return 'Email inválido';
    if (control.errors['maxlength']) {
      const { requiredLength, actualLength } = control.errors['maxlength'];
      return `Superaste el límite de ${requiredLength} caracteres (llevas ${actualLength}, sobran ${actualLength - requiredLength}).`;
    }
    return 'Campo inválido';
  }

  /** Etiquetas visibles de los campos, para nombrarlos en el mensaje de bloqueo. */
  private static readonly FIELD_LABELS: Record<string, string> = {
    commercialName: 'Nombre Comercial',
    businessName: 'Razón Social',
    description: 'Descripción',
    phone: 'Teléfono',
    fiscalAddress: 'Dirección Fiscal',
    website: 'Sitio Web',
    taxCode: 'Código Fiscal',
  };

  /**
   * inc34 — Explica POR QUÉ quedó bloqueado el guardado, nombrando los campos que
   * fallan. Sin esto el usuario solo veía que el botón no reaccionaba.
   */
  private resumenErrores(): string {
    const invalidos = Object.keys(this.form.controls)
      .filter((key) => this.form.get(key)?.invalid)
      .map((key) => EmpresaFormComponent.FIELD_LABELS[key] ?? key);

    if (invalidos.length === 0) return 'Revisa los datos del formulario.';
    return `No se pudo guardar: revisa ${invalidos.join(', ')}.`;
  }
}
